class LabVideo extends HTMLElement {
  constructor() {
    super();

    this.shadow = this.attachShadow({mode: 'open'});

    const style = document.createElement('style');
    style.textContent = `
      :host {
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
      }
      :host > video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      :host > button[data-controls] {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 0;
        background: none;
        border: none;
        pointer-events: none;
        transition: opacity 0.2s;
        z-index: 1;
      }
      :host([data-playback="playing"]) > button[data-controls] {
        opacity: 0;
      }
    `;

    this.shadowRoot.appendChild(style);
  }

  connectedCallback() {
    if (this.hasAttribute('src') || this.querySelectorAll('source').length > 0) {
      this.initVideo();
      this.createVideoPlaybackObserver();
    }
  }

  disconnectedCallback() {
    this.video.removeEventListener('click', () => {
      this.togglePlayPause();
    });
  }

  initVideo() {
    this.video = document.createElement('video');

    if (this.hasAttribute('playsinline')) {
      this.video.setAttribute('playsinline', '');
      this.removeAttribute('playsinline');
    }
    if (this.hasAttribute('autoplay')) {
      this.video.setAttribute('autoplay', '');
      this.removeAttribute('autoplay');
    }
    if (this.hasAttribute('controls')) {
      this.video.setAttribute('controls', '');
      this.removeAttribute('controls');
    }
    if (this.hasAttribute('muted')) {
      this.video.setAttribute('muted', '');
      this.removeAttribute('muted');
    }
    if (this.hasAttribute('loop')) {
      this.video.setAttribute('loop', '');
      this.removeAttribute('loop');
    }
    if (this.hasAttribute('poster')) {
      this.video.setAttribute('poster', this.getAttribute('poster'));
      this.removeAttribute('poster');
    }

    const sources = this.querySelectorAll('source');
    sources.forEach((source) => this.video.appendChild(source));

    // Controls
    this.controlsButton = document.createElement('button');
    this.controlsButton.setAttribute('data-controls', '');
    this.controlsButton.innerHTML = `
      <svg width="93" height="93" viewBox="0 0 93 93" fill="none">
        <circle cx="46.2129" cy="46.791" r="45.9062" fill="white" fill-opacity="0.3"/>
        <circle cx="46.2129" cy="46.791" r="44.4062" stroke="white" stroke-width="3"/>
        <path d="M68.9717 46.791L34.8335 66.5007L34.8335 27.0813L68.9717 46.791Z" fill="white"/>
      </svg>
    `;

    if (this.video.hasAttribute('autoplay')) {
      // Fix bug on Chrome where muted property is set to false even with muted attribute
      this.video.muted = true;
    }

    if (!this.video.hasAttribute('autoplay')) {
      this.video.addEventListener('click', () => {
        this.togglePlayPause();
      });
    }

    this.shadow.appendChild(this.video);
    this.shadow.appendChild(this.controlsButton);
  }

  togglePlayPause() {
    this.video.paused ? this.playVideo() : this.pauseVideo();
  }

  playVideo() {
    const playPromise = this.video.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error('Video playback failed:', error);
      });
    }

    this.setAttribute('data-playback', 'playing');
  }

  pauseVideo() {
    this.video.pause();

    this.setAttribute('data-playback', 'paused');
  }

  createVideoPlaybackObserver() {
    const options = {
      threshold: 0.5,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (this.video.hasAttribute('autoplay')) {
          this.playVideo();
        }
      } else {
        this.pauseVideo();
      }
    }, options);

    this.intersectionObserver.observe(this);
  }
}

customElements.define('lab-video', LabVideo);
